import { useLayoutEffect } from 'react'
import { ArrowLeft } from 'lucide-react'
import { Link } from 'react-router-dom'
import logo from '../assets/brit-logo.svg'
import Footer from '../components/Footer'

export default function PrivacyPolicyPage() {
  // Scroll to top when component mounts (useLayoutEffect runs synchronously before paint)
  useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
  }, [])

  return (
    <div className='min-h-screen bg-white w-full'>
      {/* Header */}
      <header className='bg-white border-b border-gray-200 sticky top-0 z-10 w-full'>
        <div className='mx-auto px-4 sm:px-6 lg:px-8 py-4 w-full'>
          <div className='flex items-center justify-between w-full'>
            <Link
              to='/'
              className='inline-flex items-center text-gray-600 hover:text-gray-900 transition-colors'
            >
              <ArrowLeft className='w-5 h-5 mr-2' />
              <span className='text-sm font-medium'>Back to Home</span>
            </Link>
            <Link to='/' className='flex items-center'>
              <img src={logo} alt='BritFiler Logo' className='h-8' />
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className='max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12'>
        <div className='prose prose-lg max-w-none'>
          {/* Title */}
          <h1 className='text-4xl font-bold text-gray-900 mb-4'>
            Privacy Policy
          </h1>

          {/* GDPR Introduction */}
          <section className='mb-12'>
            <h2 className='text-2xl font-bold text-gray-900 mb-4'>
              GDPR PRIVACY POLICY STATEMENT
            </h2>
            <div className='space-y-4 text-gray-700 leading-relaxed'>
              <p>
                <strong>A-NET PROFESSIONALS LIMITED</strong>
              </p>
              <p>
                <strong>THE GENERAL DATA PROTECTION REGULATION (GDPR)</strong>
              </p>
              <p>
                The General Data Protection Regulation (GDPR) takes effect on 25th May 2018, and we have updated
                our Privacy Policy to reflect these changes. The GDPR represents a significant step forward in
                enhancing individuals' control over how their data is collected, stored, and used. It strengthens and
                modernizes existing data protection laws, which were originally drafted in a non-digital era.
              </p>
              <p>
                At A-Net Professionals Limited, we have always prioritized the security and confidentiality of both
                business and personal data. Over the years, we have implemented robust processes and systems to
                ensure that we take all reasonable measures to safeguard information.
              </p>
              <p>
                By reviewing our updated Privacy Policy below, you will gain insight into your rights under the GDPR
                and how we have incorporated its requirements to reinforce our commitment to data protection.
              </p>
            </div>
          </section>

          {/* Privacy Policy */}
          <section className='mb-12'>
            <h2 className='text-2xl font-bold text-gray-900 mb-4'>
              PRIVACY POLICY
            </h2>
            
            <div className='space-y-8'>
              {/* Introduction and Terms */}
              <div>
                <h3 className='text-xl font-semibold text-gray-900 mb-3'>
                  INTRODUCTION AND TERMS
                </h3>
                <div className='space-y-3 text-gray-700 leading-relaxed'>
                  <p>
                    A-Net Professionals Limited is a registered Data Controller with the Information Commissioner's
                    Office (ICO).
                  </p>
                  <p>
                    We may update this Privacy Notice from time to time. If any amendments are made, we will provide
                    you with a copy and/or ensure it is readily accessible.
                  </p>
                  <p>
                    In cases where we act as a Data Processor on behalf of a Data Controller (e.g., for payroll services), we
                    will issue a Letter of Engagement and operate in full compliance with the GDPR regulations, adhering
                    strictly to the agreed terms.
                  </p>
                </div>
              </div>

              {/* How the Law Protects You */}
              <div>
                <h3 className='text-xl font-semibold text-gray-900 mb-3'>
                  HOW THE LAW PROTECTS YOU
                </h3>
                <div className='space-y-3 text-gray-700 leading-relaxed'>
                  <p>
                    We are committed to protecting and respecting your personal data and privacy. This Privacy and
                    Cookie Policy outlines how we collect, use, and safeguard any personal data obtained through our
                    services. Whenever you provide us with personal information, we are legally obligated to process it in
                    accordance with all applicable data protection laws.
                  </p>
                  <p>
                    In addition to this Privacy Policy, your privacy is also protected by law. The General Data Protection
                    Regulation (GDPR), which came into effect on 25th May 2018, ensures that we only use your personal
                    information when we have a valid legal basis to do so. This includes circumstances where we may
                    share your data outside the A-NET PROFESSIONALS LIMITED Group.
                  </p>
                  <p>
                    Under the law, we must have at least one of the following lawful bases for processing your personal
                    data:
                  </p>
                  <ul className='list-disc pl-6 space-y-2'>
                    <li><strong>To fulfil a contract</strong> – when we need to provide our services to you.</li>
                    <li><strong>Legal obligation</strong> – when we are required by law to process your data.</li>
                    <li><strong>Legitimate interest</strong> – when we have a business or commercial reason to use your information,
                      provided it does not override your rights and interests.</li>
                    <li><strong>Consent</strong> – when you have given explicit permission for us to process your data.</li>
                  </ul>
                  <p>
                    If we rely on legitimate interest as the basis for processing your data, we will clearly communicate
                    what that interest is and ensure that it does not unfairly impact your rights.
                  </p>
                </div>
              </div>

              {/* Your Rights */}
              <div>
                <h3 className='text-xl font-semibold text-gray-900 mb-3'>
                  Your Rights
                </h3>
                <div className='space-y-3 text-gray-700 leading-relaxed'>
                  <p>
                    Under the General Data Protection Regulation (GDPR), you have the following rights:
                  </p>
                  <ul className='list-disc pl-6 space-y-2'>
                    <li><strong>Right to be informed</strong> – We are committed to transparency in how we process your data, as outlined
                      in this Privacy Notice.</li>
                    <li><strong>Right of access</strong> – You have the right to request details of the personal data we hold about you and the
                      reasons for processing it.</li>
                    <li><strong>Right to rectification</strong> – If any of your personal data is inaccurate or outdated, we are required to
                      correct or update it.</li>
                    <li><strong>Right to restrict processing</strong> – You may request that we temporarily or permanently stop processing
                      your personal data.</li>
                    <li><strong>Right to data portability</strong> – You have the right to obtain and reuse your personal data for your own
                      purposes.</li>
                    <li><strong>Right to object</strong> – You can object to the processing of your data, particularly for direct marketing or
                      profiling purposes.</li>
                    <li><strong>Rights related to automated decision-making and profiling</strong> – We do not use automated decision-
                      making or profiling in our processing activities.</li>
                  </ul>
                </div>
              </div>

              {/* Data We Collect About You */}
              <div>
                <h3 className='text-xl font-semibold text-gray-900 mb-3'>
                  Data We Collect About You
                </h3>
                <div className='space-y-3 text-gray-700 leading-relaxed'>
                  <p>
                    We process personal data, which may include:
                  </p>
                  <ul className='list-disc pl-6 space-y-2'>
                    <li>Your (or your employer's or our client's) name, address, date of birth, family relationships, and email
                      address.</li>
                    <li>Your IP address and cookies when using our website.</li>
                    <li>Financial data, including tax information, bank account details, investments, payroll records,
                      accounting records, and statutory returns.</li>
                    <li>Identification documents such as driving licenses, passports, or birth certificates, to comply with
                      legal requirements, including anti-money laundering and anti-terrorism legislation.</li>
                  </ul>
                  <p>
                    We collect personal data through:
                  </p>
                  <ul className='list-disc pl-6 space-y-2'>
                    <li>Our website.</li>
                    <li>Direct engagement (or potential engagement) of our services.</li>
                    <li>Communications via email, telephone, post, or social media.</li>
                    <li>Networking and third-party service providers.</li>
                    <li>Publicly available resources (e.g., Companies House).</li>
                    <li>Internet tracking software, such as Google Analytics, to understand how our digital services are used
                      and improve the user experience.</li>
                  </ul>
                </div>
              </div>

              {/* How Your Data Will Be Used */}
              <div>
                <h3 className='text-xl font-semibold text-gray-900 mb-3'>
                  How Your Data Will Be Used
                </h3>
                <div className='space-y-3 text-gray-700 leading-relaxed'>
                  <p>We use your data to:</p>
                  <ul className='list-disc pl-6 space-y-2'>
                    <li>Provide services to you (or your employer or our client) under a contract, as outlined in our Letter of
                      Engagement.</li>
                    <li>Ensure our website and digital services function optimally.</li>
                    <li>Offer you relevant products, services, or information, where you have consented or where there is a
                      legitimate interest.</li>
                    <li>Enable participation in interactive features of our services.</li>
                    <li>Maintain and improve our IT infrastructure.</li>
                    <li>Notify you of service updates.</li>
                    <li>Fulfil our legal obligations, including compliance with money laundering, anti-terrorism financing,
                      and the Criminal Finance Act.</li>
                    <li>Investigate and defend potential complaints, disciplinary matters, and legal proceedings.</li>
                    <li>Invoice you for our services and resolve any fee-related disputes.</li>
                  </ul>
                  <p>
                    If you do not provide certain requested information, we may be unable to fulfill our contract with you
                    or meet legal obligations. In some cases, we may process your data without your consent, where
                    legally required or permitted. We also utilize social media and may process public comments made by
                    you.
                  </p>
                </div>
              </div>

              {/* Legitimate Interest & Marketing */}
              <div>
                <h3 className='text-xl font-semibold text-gray-900 mb-3'>
                  Legitimate Interest & Marketing
                </h3>
                <div className='space-y-3 text-gray-700 leading-relaxed'>
                  <p>
                    We may process your personal data for our legitimate business interests, provided they do not
                    override your rights and freedoms. This may include:
                  </p>
                  <ul className='list-disc pl-6 space-y-2'>
                    <li>Marketing, business development, statistical, and management purposes.</li>
                    <li>Sending information about relevant products, services, and events if you are an existing client or
                      contact.</li>
                  </ul>
                  <p>
                    You have the right to opt out of marketing communications at any time. We will never sell your data to
                    third parties for marketing purposes.
                  </p>
                </div>
              </div>

              {/* How We Share Your Data */}
              <div>
                <h3 className='text-xl font-semibold text-gray-900 mb-3'>
                  How We Share Your Data
                </h3>
                <div className='space-y-3 text-gray-700 leading-relaxed'>
                  <p>We may share your data:</p>
                  <ul className='list-disc pl-6 space-y-2'>
                    <li><strong>Within A-Net Professionals Limited</strong> – for administrative and business development purposes.</li>
                    <li><strong>With third-party service providers</strong> – including:
                      <ul className='list-circle pl-6 mt-2 space-y-1'>
                        <li>Cloud accounting and payroll providers (e.g., Xero).</li>
                        <li>Email and secure document exchange systems.</li>
                        <li>Telemarketing and research agencies.</li>
                        <li>Off-site storage and cleaning services.</li>
                      </ul>
                    </li>
                  </ul>
                  <p>
                    All third-party providers are required to implement appropriate security measures to protect your
                    personal data and may only process it according to our instructions.
                  </p>
                  <p>
                    If we transfer your data outside the European Economic Area (EEA), we will take reasonable steps to
                    ensure it receives the same level of protection, such as ensuring third-party providers comply with the
                    Privacy Shield Framework (for transfers to the U.S.).
                  </p>
                </div>
              </div>

              {/* Data Retention */}
              <div>
                <h3 className='text-xl font-semibold text-gray-900 mb-3'>
                  Data Retention
                </h3>
                <div className='space-y-3 text-gray-700 leading-relaxed'>
                  <p>
                    We will retain your personal data only for as long as necessary to fulfill its intended purpose,
                    considering:
                  </p>
                  <ul className='list-disc pl-6 space-y-2'>
                    <li>Business and service requirements.</li>
                    <li>Legal and regulatory obligations.</li>
                    <li>Lawful grounds for processing.</li>
                    <li>The nature and sensitivity of the data.</li>
                  </ul>
                </div>
              </div>

              {/* Data Deletion */}
              <div>
                <h3 className='text-xl font-semibold text-gray-900 mb-3'>
                  Data Deletion
                </h3>
                <div className='space-y-3 text-gray-700 leading-relaxed'>
                  <p>
                    Under GDPR, you have the right to request the deletion of your personal data under specific
                    circumstances. Requests must be submitted in writing to the Managing Partner and will be assessed
                    on a case-by-case basis.
                  </p>
                </div>
              </div>

              {/* Data Correction */}
              <div>
                <h3 className='text-xl font-semibold text-gray-900 mb-3'>
                  Data Correction
                </h3>
                <div className='space-y-3 text-gray-700 leading-relaxed'>
                  <p>
                    You may request corrections or updates to your personal data by contacting our Chief Operating
                    Officer in writing, clearly specifying the information that requires amendment.
                  </p>
                </div>
              </div>

              {/* Data Security */}
              <div>
                <h3 className='text-xl font-semibold text-gray-900 mb-3'>
                  Data Security
                </h3>
                <div className='space-y-3 text-gray-700 leading-relaxed'>
                  <p>
                    We implement commercially reasonable and appropriate security measures to prevent:
                  </p>
                  <ul className='list-disc pl-6 space-y-2'>
                    <li>Accidental loss.</li>
                    <li>Unauthorized access, use, alteration, or disclosure.</li>
                  </ul>
                  <p>
                    Access to personal data is restricted to employees, agents, and contractors with a business need. They
                    are required to process data in line with our instructions and confidentiality obligations.
                  </p>
                  <p>
                    In the event of a suspected data breach, we will notify you and the relevant regulatory authorities as
                    required by law.
                  </p>
                </div>
              </div>

              {/* Data Inspection (Subject Access Requests) */}
              <div>
                <h3 className='text-xl font-semibold text-gray-900 mb-3'>
                  Data Inspection (Subject Access Requests)
                </h3>
                <div className='space-y-3 text-gray-700 leading-relaxed'>
                  <p>
                    Under GDPR, you have the right to request a copy of the personal data we hold about you. Such
                    requests must be submitted in writing. We will respond within one calendar month, confirming:
                  </p>
                  <ul className='list-disc pl-6 space-y-2'>
                    <li>Whether your data is being processed.</li>
                    <li>The purpose and lawful basis of processing.</li>
                    <li>The categories of personal data held.</li>
                    <li>The recipients of your data.</li>
                    <li>A copy of the requested data in an accessible format.</li>
                  </ul>
                  <p>
                    You may need to provide identification to verify your identity before we process your request. If we do
                    not hold any data on you, we will confirm this in writing.
                  </p>
                </div>
              </div>

              {/* Changes to This Privacy Notice */}
              <div>
                <h3 className='text-xl font-semibold text-gray-900 mb-3'>
                  Changes to This Privacy Notice
                </h3>
                <div className='space-y-3 text-gray-700 leading-relaxed'>
                  <p>
                    We regularly review this Privacy Notice and recommend checking back periodically for updates. We
                    may also display this notice to you from time to time to ensure you remain informed of its contents.
                  </p>
                </div>
              </div>

              {/* Withdrawal of Consent */}
              <div>
                <h3 className='text-xl font-semibold text-gray-900 mb-3'>
                  Withdrawal of Consent
                </h3>
                <div className='space-y-3 text-gray-700 leading-relaxed'>
                  <p>
                    Where processing is based on your consent, you may withdraw it at any time. However:
                  </p>
                  <ul className='list-disc pl-6 space-y-2'>
                    <li>Withdrawal does not affect the lawfulness of prior processing.</li>
                    <li>We may still be legally required to process certain data.</li>
                    <li>Withdrawal may impact our ability to provide services to you.</li>
                  </ul>
                </div>
              </div>

              {/* Complaints */}
              <div>
                <h3 className='text-xl font-semibold text-gray-900 mb-3'>
                  Complaints
                </h3>
                <div className='space-y-3 text-gray-700 leading-relaxed'>
                  <p>
                    If you have concerns about how your data is processed, please contact us using the details provided. If
                    you remain unsatisfied, you have the right to file a complaint with the Information Commissioner's
                    Office (ICO):
                  </p>
                  <p>
                    <a
                      href='https://ico.org.uk/concerns'
                      target='_blank'
                      rel='noopener noreferrer'
                      className='text-[#001B38] hover:text-[#6941C6] underline font-medium'
                    >
                      https://ico.org.uk/concerns
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>

      {/* Footer */}
      <Footer />
    </div>
  )
}
